import React, { useEffect, useState } from 'react';
import { Upload, Input, Button, Typography, Col, Row, Spin } from 'antd';
import { STATUS } from '../../../../redux/constants';
import styles from './profile.module.sass';
import { CameraFilled } from '@ant-design/icons';
import {
  showNotification,
  simpleErrorNotification,
} from '../../../../shared-components/Notifications';
import { changeTitle } from '../../../../redux/actions/app';
import { useDispatch } from 'react-redux';
import VersionCheck from '../../../../shared-components/VersionCheck';
import cx from 'classnames';
import Panel from '../../../../shared-components/Panel';
import PermissionDeniedPage from '../../../../shared-components/PermissionDeniedPage';
import LoadingSilentPush from '../../../../shared-components/LoadingSilentPush';
import { formatEmail } from '../../../../redux/utils/email';

function Profile(props) {
  const { Title, Paragraph } = Typography;
  const { status, photoUploadStatus, photoUploadData } = props;
  const [userData, setUserData] = useState({});
  const dispatch = useDispatch();
  dispatch(changeTitle('Profile Settings', null));

  function handleChange(e, name) {
    setUserData({
      ...userData,
      [name]: formatEmail(e.target.value),
    });
  }

  function clearState() {
    setUserData(props.user);
  }

  function save() {
    const { photo, username, ...userDataMinusPhotoAndUsername } = userData;
    props.updateUserProfile(userDataMinusPhotoAndUsername);
  }

  useEffect(() => {
    document.title = 'Profile Settings - Silent Push';
    props.loadUser();
    props.loadOrganization();
    props.loadMySubscriptionPlan();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (photoUploadStatus === STATUS.LOADED) {
      setUserData({ ...userData, photo: photoUploadData });
    }
  }, [photoUploadStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === STATUS.LOADED) {
      setUserData(props.user);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  function getBase64(img, callback, uuid) {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
      callback({ photo: reader.result, uuid: uuid }),
    );
    reader.readAsDataURL(img);
  }

  const handlePhotoChange = (info) => {
    const isLessThan2MB = info.file.size / 1024 / 1024 < 5;
    if (!isLessThan2MB) {
      showNotification(
        simpleErrorNotification({
          statusCode: 400,
          message: 'Image must be smaller than 5MB!',
        }),
      );
    }
    const isJpgOrPng =
      info.file.type === 'image/jpeg' || info.file.type === 'image/png';
    if (!isJpgOrPng) {
      showNotification(
        simpleErrorNotification({
          statusCode: 400,
          message: 'You can only upload JPG/PNG pictures!',
        }),
      );
    }
    if (isJpgOrPng && isLessThan2MB) {
      getBase64(info.file.originFileObj, avatarUpload, userData['username']);
    }
  };

  function avatarUpload(photoObject) {
    //POST to photo endpoint
    //if (ableToUpload) {
    props.uploadPhoto(photoObject);
    // }
  }

  const isLoadingFinished =
    (status === STATUS.LOADED && photoUploadStatus === STATUS.LOADED) ||
    STATUS.IDLE;

  return isLoadingFinished &&
    props.mySubscriptionPlanStatus === STATUS.LOADED ? (
    props.mySubscriptionPlan?.has_access_to_org_settings ? (
      <div className={styles.profile}>
        <Panel bgcolor={'#1C3138'}>
          <Row>
            <Col className={styles.profile_picture} xs={24}>
              <Upload
                name="avatar"
                listType="picture-card"
                className={styles.profile_picture_upload}
                showUploadList={false}
                customRequest={dummyRequest}
                onChange={handlePhotoChange}
              >
                {userData['photo'] ? (
                  <>
                    <div className={styles.change}>
                      <CameraFilled />
                      <Title level={5}>Change</Title>
                    </div>
                    <div
                      className={styles.image}
                      style={{
                        background: `no-repeat center/cover transparent url(${userData[
                          'photo'
                        ].replace('http://', 'https://')})`,
                      }}
                    />
                  </>
                ) : (
                  <div className={styles.upload}>
                    <CameraFilled />
                    <Title level={5}>Upload</Title>
                  </div>
                )}
              </Upload>

              <div className={styles.titles}>
                <Title level={3}>
                  {userData['first_name']}&nbsp;{userData['last_name']}
                </Title>
                <Title level={4}>{userData['organisation']}</Title>
              </div>
            </Col>
          </Row>

          <Row className={styles.inputs} gutter={[16, 16]}>
            <Col xs={12}>
              <Paragraph className={styles.paragaph}>First Name</Paragraph>
              <Input
                data-testid={'input1001'}
                placeholder={userData['first_name']}
                value={userData['first_name']}
                onChange={(e) => handleChange(e, 'first_name')}
              ></Input>
            </Col>

            <Col xs={12}>
              <Paragraph className={styles.paragaph}>Last Name</Paragraph>
              <Input
                data-testid={'input1002'}
                placeholder={userData['last_name']}
                value={userData['last_name']}
                onChange={(e) => handleChange(e, 'last_name')}
              ></Input>
            </Col>

            <Col xs={12}>
              <Paragraph className={styles.paragaph}>Job Description</Paragraph>
              <Input
                data-testid={'input1003'}
                placeholder={userData['job_description']}
                value={userData['job_description']}
                onChange={(e) => handleChange(e, 'job_description')}
              ></Input>
            </Col>

            <Col xs={12}>
              <Paragraph className={styles.paragaph}>Organisation</Paragraph>
              <Input
                data-testid={'input1004'}
                placeholder={userData['organisation']}
                value={userData['organisation']}
                onChange={(e) => handleChange(e, 'organisation')}
              ></Input>
            </Col>
          </Row>

          <Row className={cx(styles.inputs, styles.contact_info)} gutter={[16]}>
            <Col xs={24}>
              <Paragraph className={styles.paragaph}>
                Contact Information
              </Paragraph>
            </Col>

            <Col xs={24} lg={8}>
              <Input
                data-testid={'input1005'}
                placeholder={userData['email'] ?? 'Email'}
                value={userData['email']}
                onChange={(e) => handleChange(e, 'email')}
              ></Input>
            </Col>

            <Col xs={24} lg={8}>
              <Input
                data-testid={'input1006'}
                placeholder={userData['phone'] ?? 'Phone number'}
                value={userData['phone']}
                onChange={(e) => handleChange(e, 'phone')}
              ></Input>
            </Col>

            <Col xs={24} lg={8}>
              <Input
                data-testid={'input1007'}
                placeholder={userData['address'] ?? 'Address'}
                value={userData['address']}
                onChange={(e) => handleChange(e, 'address')}
              ></Input>
            </Col>
          </Row>

          <Row className={styles.button_row}>
            <Col xs={24}>
              <Button
                onClick={() => save()}
                type="primary"
                className="tab_save"
              >
                Save
              </Button>
              <Button onClick={() => clearState()} className="tab_cancel">
                Cancel
              </Button>

              <VersionCheck parentComponentStatus={props.status} />
            </Col>
          </Row>
        </Panel>
      </div>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
}

export default Profile;
